import { create } from "zustand";
import { type Branch } from "@entities/branch";

interface BranchStore {
  branches: Branch[];
  setBranches: (branches: Branch[]) => void;
  addBranch: (branch: Branch) => void;
  updateBranch: (id: string, updates: Partial<Branch>) => void;
  deleteBranch: (id: string) => void;
  getBranchById: (id: string) => Branch | undefined;
  getBranchesByTenant: (tenantId: string) => Branch[];
  getBranchesByCategory: (categoryId: string) => Branch[];

  // Template functions
  getTemplateBranches: (tenantId: string) => Branch[];
  toggleTemplate: (id: string) => void;

  // UI state
  formOpen: boolean;
  setFormOpen: (open: boolean) => void;
  editingId: string | undefined;
  setEditingId: (id: string | undefined) => void;
  deletingId: string | undefined;
  setDeletingId: (id: string | undefined) => void;

  // Template selection UI state
  templateSelectionOpen: boolean;
  setTemplateSelectionOpen: (open: boolean) => void;

  // Template wizard UI state
  templateWizardOpen: boolean;
  setTemplateWizardOpen: (open: boolean) => void;

  // Filters
  filters: {
    searchQuery: string;
    categoryFilter: string;
  };
  setFilters: (filters: Partial<BranchStore["filters"]>) => void;
  resetFilters: () => void;
}

const defaultFilters = {
  searchQuery: "",
  categoryFilter: "all",
};

export const useBranchStore = create<BranchStore>((set, get) => ({
  branches: [],
  setBranches: (branches) => set({ branches }),
  addBranch: (branch) =>
    set((state) => ({
      branches: [...state.branches, branch],
    })),
  updateBranch: (id, updates) =>
    set((state) => ({
      branches: state.branches.map((b) =>
        b.id === id ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b
      ),
    })),
  deleteBranch: (id) =>
    set((state) => ({
      branches: state.branches.filter((b) => b.id !== id),
    })),
  getBranchById: (id) => get().branches.find((b) => b.id === id),
  getBranchesByTenant: (tenantId) => get().branches.filter((b) => b.tenant_id === tenantId),
  getBranchesByCategory: (categoryId) =>
    get().branches.filter((b) => b.branchcategory_id === categoryId),

  // Template functions
  getTemplateBranches: (tenantId) =>
    get().branches.filter((b) => b.tenant_id === tenantId && b.is_template),
  toggleTemplate: (id) =>
    set((state) => ({
      branches: state.branches.map((b) =>
        b.id === id ? { ...b, is_template: !b.is_template, updatedAt: new Date().toISOString() } : b
      ),
    })),

  // UI state
  formOpen: false,
  setFormOpen: (open) => set({ formOpen: open }),
  editingId: undefined,
  setEditingId: (id) => set({ editingId: id }),
  deletingId: undefined,
  setDeletingId: (id) => set({ deletingId: id }),

  // Template selection UI state
  templateSelectionOpen: false,
  setTemplateSelectionOpen: (open) => set({ templateSelectionOpen: open }),

  // Template wizard UI state
  templateWizardOpen: false,
  setTemplateWizardOpen: (open) => set({ templateWizardOpen: open }),

  // Filters
  filters: { ...defaultFilters },
  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),
  resetFilters: () => set({ filters: { ...defaultFilters } }),
}));
